import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * One certification row managed by `CertificationsForm`'s `useFieldArray`. Mirrors
 * `CompanyCertificationRequest` (`types/company.type.ts`) field-for-field — NOT
 * `CompanyCertificationResponse`'s `id`/`companyCertificationId`, because the pre-Carbon form
 * never sent those on save either (`newCertification`'s own state shape only ever carried
 * `certificationName`/`expirationDate`/`valid`/`fileURL`); `companyCertificationId` links a row
 * to a `CompanyCertification` catalog entry the backend supports but this UI has never populated.
 * Preserved 1:1, not a new gap this task introduces.
 */
export interface CertificationFormFields {
  certificationName: string;
  expirationDate: string;
  valid: boolean;
  fileURL: string;
}

export interface CertificationsFormFields {
  certifications: CertificationFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `certificationName` carries NO `.required()` — the pre-Carbon "Add" button's own disabled
 * condition (`disabled={isUploading || !uploadedSpecificationFileUrl}`) never checked
 * `certificationName`, and the backend's `CertificationDetailRequest.certificationName` carries
 * no `@NotBlank`, only `@Size(max = 255)` — mirrored here. Requiring it now would be a NEW
 * restriction with no pre-Carbon or backend counterpart (unlike `street`/`city`/... on
 * `addressesValidationSchema.ts`, whose gate DID cover every field it now requires).
 *
 * `fileURL` IS `.required()` — this one mirrors the pre-Carbon "Add" gate EXACTLY: no
 * certification was ever staged without `uploadedSpecificationFileUrl` already truthy, so every
 * already-persisted row already satisfies this. `.max(2000)` mirrors
 * `CertificationDetailRequest.fileURL`'s own `@Size(max = 2000)`.
 *
 * `expirationDate` IS `.required()` — this is the one rule that's a genuine ADDITION beyond the
 * pre-Carbon gate (which never checked it), but not an unsafe one: `expirationDate` is a Java
 * `LocalDate` on the backend with no `@JsonFormat`/leniency override, and Jackson's default
 * `LocalDate` deserializer rejects an empty string outright (this backend sets no
 * `ACCEPT_EMPTY_STRING_AS_NULL_OBJECT` feature) — so a pre-Carbon "Add" that left the date input
 * blank was ALREADY guaranteed to 400 on save, meaning no certification with a blank
 * `expirationDate` could ever have reached the database. Requiring it client-side now blocks
 * nothing that ever worked; it just surfaces the same failure as a form error instead of a
 * network 400, the identical rationale Task 8 used for `taxId`.
 *
 * `valid` is never user-edited by this form (no control renders it — same as pre-Carbon, where
 * `newCertification.valid` was hardcoded `true` and never exposed), so it's `.default(true)` and
 * otherwise unconstrained; `CertificationsForm.tsx` seeds it from each EXISTING certification's
 * own `valid` on load (not hardcoded), so a certification already marked invalid by whatever
 * reviews that flag doesn't silently flip back to valid on an unrelated resave.
 */
export const certificationRowValidationSchema = Yup.object({
  certificationName: Yup.string().max(255, maxLengthMessage(255)),
  expirationDate: Yup.string().required(
    i18next.t("validations.expireDateRequired")
  ),
  valid: Yup.boolean().default(true),
  fileURL: Yup.string()
    .required(i18next.t("certifications.fileRequired"))
    .max(2000, maxLengthMessage(2000)),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero certifications was, and stays, a
 * valid state) — only each staged row, at the moment it was added. No `.min()`/`.required()` on
 * the array, same rationale as `addressesValidationSchema.ts`/`bankingInformationValidationSchema.ts`.
 */
export const certificationsValidationSchema = Yup.object({
  certifications: Yup.array().of(certificationRowValidationSchema).default([]),
});
